import { Checkbox } from 'components/checkbox'
import { useMemo } from 'react'
import { capitalize } from 'lodash'

import { GdprSubmitProps } from './types'
import { Button } from '../../atoms/button'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import * as yup from 'yup'
import { TFunction } from 'i18next'

export type GdprFormValues = {
  agreeToTerms: boolean
  agreeToDataCollection: boolean
}

export const getGdprSchema = (
  t: TFunction<'translation', undefined>
): yup.ObjectSchema<GdprFormValues> => {
  return yup.object({
    agreeToTerms: yup
      .boolean()
      .default(false)
      .oneOf([true], t('welcome.gdpr.violate')),
    agreeToDataCollection: yup
      .boolean()
      .default(false)
      .oneOf([true], t('welcome.gdpr.data_consent_violate'))
  })
}

export function GdprSubmit({ submitting }: GdprSubmitProps) {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors }
  } = useFormContext<GdprFormValues>()

  const errorsMessages = useMemo(() => {
    return [...Object.values(errors).map((error) => error.message)]
  }, [errors])

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex flex-col items-start gap-2">
        <Controller
          name="agreeToTerms"
          control={control}
          render={({ field }) => (
            <Checkbox
              inputRef={field.ref}
              onChange={field.onChange}
              checked={field.value}
              hasError={!!errors[field.name]}
              label={
                <span className="text-gray-500">
                  {t('welcome.gdpr.privacy_policy.text')}{' '}
                  <a
                    href="https://www.termsfeed.com/live/ffb61d4f-8d43-4f89-b79f-6a01c6a65b86"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('welcome.gdpr.privacy_policy.link')}
                  </a>
                </span>
              }
            />
          )}
        />

        <Controller
          name="agreeToDataCollection"
          control={control}
          render={({ field }) => (
            <Checkbox
              inputRef={field.ref}
              onChange={field.onChange}
              checked={field.value}
              hasError={!!errors[field.name]}
              label={
                <span className="text-gray-500">
                  {t('welcome.gdpr.data_consent')}
                </span>
              }
            />
          )}
        />
      </div>

      <div className="flex w-full flex-col items-center gap-4">
        <Button
          className="w-full"
          variant="default"
          type="submit"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <svg
                className="-ml-1 mr-3 size-5 animate-spin text-gray-800"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>

                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>

              {t('welcome.buttons.submit.loading')}
            </>
          ) : (
            t('welcome.buttons.submit.text')
          )}
        </Button>

        {!!errorsMessages.length && (
          <p className="self-start text-left text-gray-500">
            {capitalize(errorsMessages.join(', '))}
          </p>
        )}
      </div>
    </div>
  )
}
